/**
 * Mail domains people use for themselves. Muting one would mute everyone on
 * it, so only the address is offered there.
 */
const PERSONAL_MAIL_DOMAINS = new Set([
  "gmail.com",
  "googlemail.com",
  "outlook.com",
  "hotmail.com",
  "live.com",
  "msn.com",
  "yahoo.com",
  "ymail.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "aol.com",
  "proton.me",
  "protonmail.com",
  "pm.me",
  "fastmail.com",
  "hey.com",
  "gmx.com",
  "gmx.net",
  "mail.com",
  "zoho.com",
  "yandex.com",
  "comcast.net",
  "att.net",
  "verizon.net",
]);

/**
 * Personal mail providers under many country suffixes: yahoo.co.uk,
 * outlook.fr, gmx.de. Matched by the name before the suffix.
 */
const PERSONAL_MAIL_PROVIDERS = new Set([
  "yahoo",
  "hotmail",
  "outlook",
  "live",
  "msn",
  "gmx",
  "web",
  "t-online",
  "orange",
  "wanadoo",
  "free",
  "laposte",
  "libero",
  "virgilio",
  "qq",
  "163",
  "126",
  "sina",
  "naver",
  "daum",
  "hanmail",
  "rediffmail",
  "yandex",
  "mail",
  "rambler",
  "btinternet",
  "sky",
  "virginmedia",
  "talktalk",
  "bigpond",
  "optusnet",
  "shaw",
  "rogers",
  "sympatico",
  "sbcglobal",
  "bellsouth",
  "cox",
  "charter",
  "earthlink",
  "rocketmail",
  "seznam",
  "wp",
  "o2",
]);

/** Whether a domain is people's own mail, or a university's, shared by many. */
function isSharedMailDomain(domain: string): boolean {
  if (PERSONAL_MAIL_DOMAINS.has(domain)) return true;
  const labels = domain.split(".");
  // A provider's own domain: its name, then a suffix of one or two labels.
  if (labels.length <= 3 && PERSONAL_MAIL_PROVIDERS.has(labels[0] ?? "")) return true;
  return /\.edu(\.[a-z]{2})?$|\.ac\.[a-z]{2}$/.test(domain);
}

export type MuteChoice = { value: string; label: string };

/**
 * What a task's asker can be muted as: the address, and its whole domain when
 * that is a company's — "everything from micro.so", never "everything from
 * gmail.com", and never the user's own company. "Mute" is the word throughout:
 * it is what the confirmation has always said, and one name for one thing
 * beats describing the effect differently at each step.
 *
 * Not "permanently", though it outlasts the row it was tapped from: every mute
 * is listed under the header's sliders with an Unmute beside it, so calling it
 * permanent would promise something the next screen contradicts. Nothing when
 * no mail sender asked. While the user's own domains are not known (`undefined`), no domain
 * is offered: it could be theirs.
 */
export function muteChoices(
  askedBy: string | null | undefined,
  ownDomains: readonly string[] | undefined,
): MuteChoice[] {
  const address = askedBy?.trim().toLowerCase();
  const domain = address?.match(/^[^@\s]+@([^@\s]+\.[^@\s]+)$/)?.[1];
  if (!address || !domain) return [];
  const choices = [{ value: address, label: `Mute ${address}` }];
  const own = !ownDomains || ownDomains.some((d) => d === domain || d.endsWith(`.${domain}`));
  if (!own && !isSharedMailDomain(domain)) {
    choices.push({ value: domain, label: `Mute all from ${domain}` });
  }
  return choices;
}

/**
 * What a mute asks before it happens.
 *
 * The row says what the action is; this says what it will do, which is the
 * part worth stopping for: a mute reaches backwards, and every task that
 * sender has already asked for goes with it. Without this the user learned
 * the second half from the line after, once the tasks had gone.
 *
 * Not "permanently", for the reason {@link muteChoices} gives: every mute is
 * listed with an Unmute beside it, so the word would promise something the
 * next screen contradicts.
 *
 * The address lives here rather than in the row. A menu sized to an arbitrary
 * string pushed the card past the window and truncated the very thing the
 * user was about to act on; a dialog has the width to say it in full.
 */
export function muteConfirm(choice: MuteChoice): { title: string; body: string } {
  // A sender is an address and a domain is not, which is the whole of the
  // difference, and the one `muteChoices` draws when it builds the pair.
  const sender = choice.value.includes("@");
  if (sender) {
    return {
      title: `Mute ${choice.value}?`,
      body: "Okthx will stop making tasks from them, and will drop any it has already made.",
    };
  }
  return {
    title: `Mute all from ${choice.value}?`,
    body: `Okthx will stop making tasks from anyone at ${choice.value}, and will drop any it has already made.`,
  };
}

/**
 * What the mute confirmation says, with the count the server reports.
 *
 * Built here rather than on each platform: the sentence is the only place the
 * user learns how much a mute just took off their list, and it should read the
 * same on web and on a phone.
 */
export function mutedLine(value: string, dropped: number): string {
  if (dropped === 0) return `Muted ${value}.`;
  return `Muted ${value}. Dropped ${dropped} ${dropped === 1 ? "task" : "tasks"}.`;
}
